import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useParams } from 'react-router-dom';
import {
  FaVideo,
  FaFileAlt,
  FaHeadphones,
  FaImage,
  FaHeart,
  FaChartBar,
  FaLock,
  FaCheckCircle,
  FaMobile,
  FaBell,
  FaBullseye,
  FaGlobe,
  FaExchangeAlt,
  FaHandPointer,
  FaDownload,
  FaUsers,
  FaEnvelope,
  FaArrowRight
} from 'react-icons/fa';
import Breadcrumb from '../components/Breadcrumb.jsx';
import KeywordTopics from '../components/KeywordTopics.jsx';
import {
  trackSignupFree,
  trackStartFree,
  trackWatchDemo,
  useYouTubeIframeAnalytics,
  withYouTubeAnalyticsParams
} from '../utils/analytics.js';
import { setPageSEO } from '../utils/seo.js';
import { getKeywordCluster } from '../utils/keywordClusters.js';
import './Features.css';

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
};

const staggerChildren = {
  visible: {
    transition: { staggerChildren: 0.1 }
  }
};

const signupUrl = '/signup';
const demoEmbedUrl = 'https://www.youtube.com/embed/9ofMDlTQF0A';

const implementationChecks = [
  ['Define the publishing goal', 'Choose the buyer question, page, or campaign this feature should support before collecting new material.'],
  ['Set an approval owner', 'Assign responsibility for checking attribution, consent, clarity, and brand suitability before anything goes live.'],
  ['Keep the request focused', 'Ask only for the context and outcome needed to create useful proof, and let customers respond from any device.'],
  ['Review performance regularly', 'Track which stories are published, where they appear, and when they should be refreshed or replaced.']
];

const featurePages = [
  {
    slug: 'review-widgets', title: 'Review Widgets', eyebrow: 'GOOGLE REVIEW WIDGETS', keyword: 'review widget',
    icon: <FaChartBar className="icon" />,
    description: 'Display your best reviews in polished, responsive widgets that build trust when visitors are ready to act.',
    benefits: ['Google review widget layouts', 'Automatic review updates', 'Responsive on every screen', 'Fast no-code installation'],
    outcomes: ['Bring trusted reviews on-site', 'Keep social proof fresh', 'Match widgets to your brand', 'Improve buyer confidence'],
    useCases: ['Homepage trust sections', 'Product and service pages', 'High-intent landing pages']
  },
  {
    slug: 'testimonial-website-widgets', title: 'Website Widgets', eyebrow: 'TESTIMONIAL WEBSITE WIDGETS', keyword: 'website testimonials',
    icon: <FaGlobe className="icon" />,
    description: 'Turn customer stories into testimonial sections, scrolling displays, rotators, and cards designed for your website.',
    benefits: ['Grid, carousel, and masonry layouts', 'Scrolling testimonial displays', 'Flexible testimonial cards', 'Embed on any website'],
    outcomes: ['Create stronger testimonial sections', 'Show proof without slowing pages', 'Control colors and layouts', 'Update every embed automatically'],
    useCases: ['Website testimonial sections', 'Scrolling testimonials', 'Testimonial rotators']
  },
  {
    slug: 'review-management-software', title: 'Review Management', eyebrow: 'REVIEW MANAGEMENT SOFTWARE', keyword: 'review management platform',
    icon: <FaFileAlt className="icon" />,
    description: 'Collect, organize, approve, and publish customer reviews from one practical review management platform.',
    benefits: ['Central review management tools', 'Simple approval workflow', 'Search and organize feedback', 'Publish approved reviews faster'],
    outcomes: ['Stop losing reviews in inboxes', 'Give teams one review library', 'Protect publishing quality', 'Reuse feedback across campaigns'],
    useCases: ['Google review management', 'Multi-location review workflows', 'Marketing approval processes']
  },
  {
    slug: 'customer-feedback-software', title: 'Customer Feedback', eyebrow: 'CUSTOMER FEEDBACK SOFTWARE', keyword: 'customer feedback management',
    icon: <FaEnvelope className="icon" />,
    description: 'Create focused customer feedback forms that make it easy to gather useful, actionable responses from clients.',
    benefits: ['Custom feedback forms', 'Video, text, and audio responses', 'Mobile-friendly collection', 'Central feedback management'],
    outcomes: ['Ask clearer questions', 'Capture feedback while it is fresh', 'Spot recurring customer themes', 'Turn feedback into proof'],
    useCases: ['Post-purchase feedback', 'Client feedback forms', 'Service experience surveys']
  },
  {
    slug: 'wall-of-love-testimonials', title: 'Wall of Love', eyebrow: 'TESTIMONIAL WALL', keyword: 'testimonial page',
    icon: <FaHeart className="icon" />,
    description: 'Build a testimonial page that brings your strongest customer stories together in one shareable destination.',
    benefits: ['Beautiful testimonial wall layouts', 'Custom testimonial page design', 'Video and text support', 'Shareable public URL'],
    outcomes: ['Make customer love visible', 'Create a proof destination', 'Support sales conversations', 'Keep new stories flowing'],
    useCases: ['Customer love pages', 'Launch and campaign proof', 'Sales follow-up links']
  },
  {
    slug: 'widgets', title: 'Widgets', eyebrow: 'SOCIAL PROOF WIDGETS', keyword: 'social proof widget',
    icon: <FaExchangeAlt className="icon" />,
    description: 'Add lightweight social proof widgets wherever buyers hesitate, compare options, or decide whether to trust you.',
    benefits: ['Multiple widget formats', 'Responsive embeds', 'Automatic content updates', 'No-code customization'],
    outcomes: ['Add proof to key pages', 'Reduce buyer hesitation', 'Reuse one testimonial library', 'Launch without developers'],
    useCases: ['Landing page proof', 'Pricing page widgets', 'Product page testimonials']
  },
  {
    slug: 'testimonial-generator', title: 'Testimonial Generator', eyebrow: 'TESTIMONIAL GENERATOR', keyword: 'testimonial generator',
    icon: <FaBullseye className="icon" />,
    description: 'Guide customers with thoughtful prompts and turn their responses into clear, credible testimonials ready to publish.',
    benefits: ['Guided testimonial prompts', 'Easy customer response flow', 'Review before publishing', 'Ready-to-use testimonial copy'],
    outcomes: ['Avoid vague customer praise', 'Collect outcome-focused stories', 'Reduce editing time', 'Create consistent proof'],
    useCases: ['B2B customer stories', 'Creator testimonials', 'Service business reviews']
  },
  {
    slug: 'ai-testimonial-generator', title: 'AI Testimonials', eyebrow: 'AI TESTIMONIAL GENERATOR', keyword: 'AI testimonial generator',
    icon: <FaVideo className="icon" />,
    description: 'Use AI-assisted workflows to shape raw customer feedback into concise testimonials while keeping the customer meaning intact.',
    benefits: ['AI-assisted testimonial drafts', 'Faster feedback cleanup', 'Consistent tone and structure', 'Human review before publishing'],
    outcomes: ['Turn raw feedback into usable copy', 'Save time on editing', 'Preserve authentic customer meaning', 'Publish polished proof faster'],
    useCases: ['Long-form feedback cleanup', 'Campaign testimonial drafts', 'Multi-channel social proof']
  }
];

function FeaturesCatalog() {
  const [activeAccordion, setActiveAccordion] = useState(0);
  const [showDemoModal, setShowDemoModal] = useState(false);
  const demoSectionIframeRef = useYouTubeIframeAnalytics('Clientalio Features Demo');
  const demoModalIframeRef = useYouTubeIframeAnalytics('Clientalio Features Demo Modal', showDemoModal);

  useEffect(() => setPageSEO({
    title: 'Clientalio Features - Turn Customer Love Into Growth',
    description: 'Use Clientalio to build trust before sales calls, turn customer stories into social proof, and help more buyers choose your business.',
    canonical: 'https://clientalio.com/features',
    ogTitle: 'Clientalio Features - Turn Customer Love Into Growth',
    ogDescription: 'Build trust before sales calls and make customer love visible where buyers decide.',
    twitterTitle: 'Clientalio Features - Turn Customer Love Into Growth',
    twitterDescription: 'Use customer stories to build trust, increase conversions, and help more buyers choose your business.'
  }), []);

  useEffect(() => {
    if (!showDemoModal) return undefined;

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        setShowDemoModal(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [showDemoModal]);

  const whyBusinessesLove = [
    {
      title: "5.7k+ Users Social Community",
      description: "5.7k+ Users join over Social Community that Trust Clientalio",
      stat: "5.7k+"
    },
    {
      title: "2.5x Higher Conversion Rates",
      description: "Testimonials increase trust and drive conversions. See significant uplift in sales.",
      stat: "2.5x"
    },
    {
      title: "99.9% Uptime Guarantee",
      description: "Enterprise-grade infrastructure ensures your testimonials are always available.",
      stat: "99.9%"
    },
    {
      title: "24/7 Customer Support",
      description: "Our support team is always ready to help you maximize your testimonial strategy.",
      stat: "24/7"
    },
  ];

  const faqs = [
    {
      question: "Do I need technical skills to use Clientalio?",
      answer: "Not at all! Clientalio is designed for non-technical users. Our intuitive interface makes it easy for anyone to collect, manage, and display testimonials. No coding required."
    },
    {
      question: "How do I collect testimonials from my customers?",
      answer: "Simply create a collection campaign, send a link to your customers via email or by embedding a form on your website. Customers can respond with a video, text, or audio testimonial in minutes."
    },
    {
      question: "Can I customize the testimonial widgets?",
      answer: "Absolutely! Customize everything from colors and fonts to the layout and styles. Use our no-code customizer or employ our developers for advanced customization."
    },
    {
      question: "Can I embed testimonials on my website?",
      answer: "Yes! Embed testimonials anywhere with a simple copy-paste code. We support all major platforms including WordPress, Shopify, Wix, Webflow, and more."
    },
    {
      question: "How secure is my customer data?",
      answer: "Your data is protected with enterprise-grade SSL encryption. We're GDPR compliant and follow the highest security standards in the industry."
    },
    {
      question: "What if I need help with integration?",
      answer: "Our support team can help with any integration questions. For WordPress, Shopify, and other major platforms, we have detailed guides and tutorials available."
    }
  ];

  return (
    <div className="features-page">
      {/* Hero Section */}
      <motion.section 
        className="features-hero"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.8 }}
      >
        <div className="container features-hero-layout">
          <motion.div
            className="features-hero-copy"
            variants={fadeInUp}
            initial="hidden"
            animate="visible"
          >
            <span className="features-hero-eyebrow">THE CUSTOMER PROOF TOOLKIT</span>
            <h1>Turn Customer Love Into Business Growth</h1>
            <p>Build trust before your first sales call, make proof visible where buyers decide, and give people a reason to choose you today.</p>
            <div className="hero-actions">
              <a className="btn-primary" href={signupUrl} target="_blank" rel="noreferrer" onClick={() => trackStartFree('features_hero')}>Start FREE In Under 2 Minutes</a>
              <button className="btn-secondary" type="button" onClick={() => { trackWatchDemo('features_hero'); setShowDemoModal(true); }}>Watch 30 Sec Demo</button>
            </div>
            <div className="features-hero-trust">
              <span><FaCheckCircle /> No customer login</span>
              <span><FaCheckCircle /> No-code publishing</span>
              <span><FaCheckCircle /> Review before launch</span>
            </div>
          </motion.div>
          <motion.aside
            className="features-hero-board"
            initial={{ opacity: 0, x: 28 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.65 }}
          >
            <div className="features-board-header"><span>Clientalio toolkit</span><strong>8 features</strong></div>
            <div className="features-board-grid">
              {featurePages.slice(0, 4).map((feature) => (
                <Link to={`/features/${feature.slug}`} key={feature.slug}>
                  <span>{feature.icon}</span>
                  <strong>{feature.title}</strong>
                </Link>
              ))}
            </div>
            <Link className="features-board-link" to="#feature-directory">Explore the complete toolkit <FaArrowRight /></Link>
          </motion.aside>
        </div>
      </motion.section>

      {showDemoModal && (
        <div className="features-demo-modal" role="dialog" aria-modal="true" aria-label="Clientalio features demo video">
          <button
            className="features-demo-modal-backdrop"
            type="button"
            aria-label="Close demo video"
            onClick={() => setShowDemoModal(false)}
          />
          <div className="features-demo-modal-content">
            <button
              className="features-demo-modal-close"
              type="button"
              aria-label="Close demo video"
              onClick={() => setShowDemoModal(false)}
            >
              ×
            </button>
            <iframe
              ref={demoModalIframeRef}
              src={withYouTubeAnalyticsParams(`${demoEmbedUrl}?autoplay=1`)}
              title="Clientalio Features Demo"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      )}



      {/* Feature Grid Section */}
      <motion.section 
        id="feature-directory"
        className="features-list"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>What You Gain After Signup</h2>
            <p>Create trust assets, ask customers faster, and use social proof before more buyers leave undecided.</p>
          </motion.div>
          <motion.div 
            className="features-grid-page"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {featurePages.map((feature) => (
              <motion.div 
                key={feature.slug}
                className="feature-card-detailed"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
              >
                <Link className="feature-card-content" to={`/features/${feature.slug}`}>
                  <div className="feature-icon-large">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p className="feature-description">{feature.description}</p>
                  <ul className="feature-benefits">
                    {feature.benefits.map((benefit) => <li key={benefit}>✓ {benefit}</li>)}
                  </ul>
                  <span className="feature-card-action">Explore {feature.title} →</span>
                </Link>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Why Businesses Love Clientalio */}
      <motion.section 
        className="why-love-section"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <motion.div className="section-header" variants={fadeInUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
            <h2>Why Trust Becomes a Growth Channel</h2>
            <p>Your competitors are already using customer proof. Doing nothing means your best stories stay invisible.</p>
          </motion.div>
          <motion.div 
            className="why-grid"
            variants={staggerChildren}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.1 }}
          >
            {whyBusinessesLove.map((item, index) => (
              <motion.div 
                key={index} 
                className="why-card"
                variants={fadeInUp}
                whileHover={{ y: -8 }}
              >
                <div className="stat-highlight">{item.stat}</div>
                <h3>{item.title}</h3>
                <p>{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </motion.section>

      {/* Video Demo Section */}
      <motion.section
        className="features-video-section"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.1 }}
      >
        <div className="container">
          <div className="section-header">
            <h2>See How Fast Proof Becomes Visible</h2>
            <p>Watch how a customer story can become a trust signal for your website.</p>
          </div>
          <div className="video-wrapper">
            <iframe
              ref={demoSectionIframeRef}
              src={withYouTubeAnalyticsParams(demoEmbedUrl)}
              title="Clientalio Features Demo"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            ></iframe>
          </div>
        </div>
      </motion.section>

      {/* Final CTA Section */}
      <motion.section 
        className="features-cta"
        variants={fadeInUp}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true, amount: 0.3 }}
      >
        <div className="container">
          <motion.div
            variants={fadeInUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <h2>Ready to Build Trust Before Your Next Buyer Decides?</h2>
            <p>In less than 2 minutes you can create your branded page, share your first request, and start turning customer love into growth.</p>
            <div className="cta-actions">
              <a className="btn-primary" href={signupUrl} onClick={() => trackSignupFree('features_cta')}>Start FREE In Under 2 Minutes</a>
            </div>
            <p className="cta-trust">✓ Branded page  ✓ First customer request  ✓ No credit card required</p>
          </motion.div>
        </div>
      </motion.section>
    </div>
  );
}

function FeatureDetail({ feature }) {
  useEffect(() => setPageSEO({
    title: `${feature.title} | Clientalio`,
    description: `${feature.description} Use Clientalio as your ${feature.keyword}.`,
    keywords: getKeywordCluster(feature.slug),
    canonical: `https://clientalio.com/features/${feature.slug}`,
    ogTitle: `${feature.title} by Clientalio`,
    ogDescription: feature.description,
    twitterTitle: `${feature.title} | Clientalio`,
    twitterDescription: feature.description
  }), [feature]);

  return (
    <main className="feature-detail-page">
      <div className="feature-detail-breadcrumb">
        <div className="container"><Breadcrumb currentLabel={feature.title} /></div>
      </div>

      <section className="feature-detail-hero">
        <div className="container feature-detail-hero-grid">
          <motion.div initial="hidden" animate="visible" variants={fadeInUp}>
            <span className="feature-detail-eyebrow">{feature.eyebrow}</span>
            <h1>{feature.title} that turn customer proof into action</h1>
            <p>{feature.description}</p>
            <div className="feature-detail-actions">
              <a className="btn-primary" href={signupUrl} onClick={() => trackStartFree(`feature_${feature.slug}`)}>Start collecting for free</a>
              <Link to="/features" className="feature-detail-secondary">Explore all features</Link>
            </div>
            <p className="feature-detail-trust">No credit card required · Set up in minutes · No coding needed</p>
          </motion.div>
          <motion.div className="feature-detail-preview" initial={{ opacity: 0, x: 24 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.6 }}>
            <div className="feature-detail-preview-icon">{feature.icon}</div>
            <span>Built for credible customer proof</span>
            <h2>{feature.title}</h2>
            <ul>{feature.benefits.map((benefit) => <li key={benefit}><FaCheckCircle /> {benefit}</li>)}</ul>
          </motion.div>
        </div>
      </section>

      <KeywordTopics slug={feature.slug} />

      <section className="feature-detail-outcomes">
        <div className="container">
          <div className="feature-detail-heading">
            <span>WHAT YOU CAN DO</span>
            <h2>Everything you need from your {feature.keyword}</h2>
            <p>Move from scattered customer feedback to organized, useful proof without adding a complicated workflow.</p>
          </div>
          <div className="feature-detail-outcome-grid">
            {feature.outcomes.map((outcome, index) => (
              <article key={outcome}><strong>0{index + 1}</strong><h3>{outcome}</h3><p>{feature.benefits[index]}. Use this capability to keep customer proof relevant, controlled, and ready for {feature.useCases[index % feature.useCases.length].toLowerCase()}.</p></article>
            ))}
          </div>
        </div>
      </section>

      <section className="feature-detail-workflow">
        <div className="container feature-detail-workflow-grid">
          <div><span className="feature-detail-eyebrow">A PRACTICAL WORKFLOW</span><h2>From customer response to published proof</h2><p>Clientalio keeps collection, review, and publishing connected so your strongest customer stories do not get lost.</p></div>
          <div className="feature-detail-steps">
            <article><span>1</span><div><h3>Collect</h3><p>Share a simple request and let customers respond from any device.</p></div></article>
            <article><span>2</span><div><h3>Review</h3><p>Choose the strongest responses and approve exactly what gets published.</p></div></article>
            <article><span>3</span><div><h3>Showcase</h3><p>Use polished customer proof on the pages and campaigns that influence decisions.</p></div></article>
          </div>
        </div>
      </section>

      <section className="feature-detail-use-cases">
        <div className="container">
          <div className="feature-detail-heading"><span>USE IT WHERE IT MATTERS</span><h2>Designed for real customer journeys</h2></div>
          <div className="feature-detail-use-grid">
            {feature.useCases.map((useCase, index) => <article key={useCase}><FaBullseye /><h3>{useCase}</h3><p>Use {feature.title.toLowerCase()} to support this moment with {feature.outcomes[index % feature.outcomes.length].toLowerCase()}, clear attribution, and proof matched to the buyer’s next decision.</p></article>)}
          </div>
        </div>
      </section>

      <section className="feature-detail-outcomes">
        <div className="container">
          <div className="feature-detail-heading"><span>IMPLEMENT WITH CONFIDENCE</span><h2>A practical launch checklist</h2><p>Build a repeatable system around {feature.title.toLowerCase()} instead of treating customer proof as a one-time campaign.</p></div>
          <div className="feature-detail-outcome-grid">
            {implementationChecks.map(([title, description], index) => <article key={title}><strong>0{index + 1}</strong><h3>{title}</h3><p>{description}</p></article>)}
          </div>
        </div>
      </section>

      <section className="feature-detail-cta">
        <div className="container"><h2>Ready to put {feature.title.toLowerCase()} to work?</h2><p>Create your free Clientalio account and publish your first customer proof without waiting on a developer.</p><a className="btn-primary" href={signupUrl} onClick={() => trackSignupFree(`feature_${feature.slug}_cta`)}>Start free today</a></div>
      </section>
    </main>
  );
}

function Features() {
  const { slug } = useParams();
  const feature = featurePages.find((item) => item.slug === slug);

  return feature ? <FeatureDetail feature={feature} /> : <FeaturesCatalog />;
}

export default Features;
